import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Upload, Image, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';

const steps = [
  { id: 'basics', label: 'Basics' },
  { id: 'location', label: 'Location' },
  { id: 'media', label: 'Media' },
  { id: 'tickets', label: 'Tickets' }
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 ${
          step.id === currentStep ? 'text-primary' : 
          steps.findIndex(s => s.id === currentStep) > index ? 'text-primary' : 'text-muted-foreground'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step.id === currentStep 
              ? 'bg-primary text-primary-foreground' 
              : steps.findIndex(s => s.id === currentStep) > index
              ? 'bg-primary/30 text-primary'
              : 'bg-secondary'
          }`}>
            {index + 1}
          </div>
          <span className="hidden md:inline text-sm">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <div className="flex-1 h-px bg-border max-w-[60px]" />
        )}
      </React.Fragment>
    ))}
  </div>
);

export function AdminCreateEventMedia() {
  const navigate = useNavigate();
  const [coverImage, setCoverImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  const handleNext = () => {
    navigate('/admin/events/new/tickets');
  };

  const handleBack = () => {
    navigate('/admin/events/new/location');
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Events
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Event Media</h1>
        <p className="text-muted-foreground mb-6">Upload images for your event</p>

        <StepIndicator currentStep="media" />

        <Card className="bg-card border-border p-6">
          <div className="space-y-6">
            {/* Cover Image */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Cover Image</label>
              <p className="text-xs text-muted-foreground mb-4">This will be the main image shown on your event page</p>
              
              {coverImage ? (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-secondary">
                  <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                  <button 
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                    onClick={() => setCoverImage(null)}
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <div 
                  className="aspect-video border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setCoverImage('https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800&q=80')}
                >
                  <Upload className="w-10 h-10 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</p>
                </div>
              )}
            </div>

            {/* Gallery Images */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Gallery Images</label>
              <p className="text-xs text-muted-foreground mb-4">Add additional images for your event gallery</p>
              
              <div className="grid grid-cols-3 gap-4">
                {galleryImages.map((img, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-secondary">
                    <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                    <button 
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                      onClick={() => setGalleryImages(galleryImages.filter((_, i) => i !== index))}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                
                <div 
                  className="aspect-square border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setGalleryImages([...galleryImages, 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&q=80'])}
                >
                  <Plus className="w-6 h-6 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground mt-1">Add Image</p>
                </div>
              </div>
            </div>

            {/* Video URL */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Promotional Video (Optional)</label>
              <div className="flex gap-2">
                <input 
                  type="url"
                  placeholder="Enter YouTube or Vimeo URL"
                  className="flex-1 bg-secondary border border-border rounded-lg px-4 py-2 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
                <Button variant="outline" className="border-border">
                  <Image className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-border"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button 
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleNext}
              >
                Continue to Tickets
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminCreateEventMedia;
